import { useState, useEffect } from 'react';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent,
  Badge, 
} from '@machinemetrics/mm-react-components';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  AlertTriangle, 
  DollarSign, 
  Package,
  Activity,
  Gauge,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { useCompany } from './CompanyLayout';

const API_BASE = '/machinesim/api';

// KPI Card component
function KPICard({ title, value, unit, icon: Icon, status, target, description }) {
  const getStatusColor = () => {
    switch (status) {
      case 'good': return 'text-green-500';
      case 'warning': return 'text-yellow-500';
      case 'critical': return 'text-red-500';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;
    const variants = {
      good: 'default',
      warning: 'secondary',
      critical: 'destructive',
    };
    return (
      <Badge variant={variants[status] || 'outline'} className="ml-2 text-xs">
        {status === 'good' ? 'On Track' : status === 'warning' ? 'Monitor' : 'Action Needed'}
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription className="flex items-center gap-2">
            {title}
            {getStatusBadge()}
          </CardDescription>
          <Icon className={`w-5 h-5 ${getStatusColor()}`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-1">
          <span className={`text-3xl font-bold ${getStatusColor()}`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
          {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
        </div>
        {target && (
          <p className="text-xs text-muted-foreground mt-1">
            Target: {target}{unit}
          </p>
        )}
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

// Simple bar chart visualization
function SimpleBarChart({ data, label }) {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-16">{item.label}</span>
          <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${(item.value / maxValue) * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium w-12 text-right">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// OEE Gauge visualization
function OEEGauge({ oee, availability, performance, quality }) {
  const getColor = (value) => {
    if (value >= 85) return 'bg-green-500';
    if (value >= 70) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full ${getColor(oee)} bg-opacity-20 border-4 ${getColor(oee).replace('bg-', 'border-')}`}>
          <span className="text-2xl font-bold">{oee.toFixed(1)}%</span>
        </div>
        <p className="text-sm text-muted-foreground mt-2">Overall OEE</p>
      </div>
      
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className={`text-lg font-semibold ${availability >= 95 ? 'text-green-500' : availability >= 85 ? 'text-yellow-500' : 'text-red-500'}`}>
            {availability.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground">Availability</p>
        </div>
        <div>
          <div className={`text-lg font-semibold ${performance >= 95 ? 'text-green-500' : performance >= 85 ? 'text-yellow-500' : 'text-red-500'}`}>
            {performance.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground">Performance</p>
        </div>
        <div>
          <div className={`text-lg font-semibold ${quality >= 99 ? 'text-green-500' : quality >= 95 ? 'text-yellow-500' : 'text-red-500'}`}>
            {quality.toFixed(1)}%
          </div>
          <p className="text-xs text-muted-foreground">Quality</p>
        </div>
      </div>
    </div>
  );
}

export default function CompanyMetrics() {
  const { company } = useCompany();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchKPIs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/metrics/${company?.id || 'dev-company-1'}/kpis`);
      const data = await response.json();
      
      if (data.success) {
        setKpis(data.kpis);
        setLastUpdated(data.lastUpdated);
      }
    } catch (error) {
      console.error('Failed to fetch KPIs:', error);
      // Use mock data on error
      setKpis({
        oee: { value: 78.5, target: 85, status: 'warning' },
        availability: { value: 92.3, target: 95, status: 'warning' },
        performance: { value: 88.7, target: 95, status: 'warning' },
        quality: { value: 96.2, target: 99, status: 'warning' },
        throughput: { value: 42.5, unit: 'parts/hour' },
        partsProduced: { value: 1247, unit: 'parts' },
        revenue: { value: 62350, unit: '$' },
        cost: { value: 45200, unit: '$' },
        profit: { value: 17150, unit: '$', status: 'good' },
        faults: { value: 2, status: 'warning' },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPIs();
    // Refresh every 30 seconds
    const interval = setInterval(fetchKPIs, 30000);
    return () => clearInterval(interval);
  }, [company?.id]);

  if (loading && !kpis) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Mock production data for chart
  const productionData = [
    { label: '6 AM', value: 32 },
    { label: '8 AM', value: 45 },
    { label: '10 AM', value: 42 },
    { label: '12 PM', value: 38 },
    { label: '2 PM', value: 48 },
    { label: '4 PM', value: 44 },
  ];

  // Machine status mock data
  const machineStatusData = [
    { label: 'Running', value: 5 },
    { label: 'Idle', value: 2 },
    { label: 'Setup', value: 1 },
    { label: 'Faulted', value: kpis?.faults?.value || 0 },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Metrics Dashboard</h1>
          <p className="text-muted-foreground">
            Key performance indicators for {company?.name}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {lastUpdated && (
            <span>Updated: {new Date(lastUpdated).toLocaleTimeString()}</span>
          )}
          <button 
            onClick={fetchKPIs}
            className="p-2 hover:bg-muted rounded-md transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Primary KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <KPICard
          title="Overall OEE"
          value={kpis?.oee?.value || 0}
          unit="%"
          icon={Gauge}
          status={kpis?.oee?.status}
          target={kpis?.oee?.target}
        />
        <KPICard
          title="Parts Produced"
          value={kpis?.partsProduced?.value || 0}
          unit="parts"
          icon={Package}
          description="Total parts this shift"
        />
        <KPICard
          title="Revenue"
          value={kpis?.revenue?.value || 0}
          unit="$"
          icon={DollarSign}
          status={kpis?.profit?.status}
          description={`Profit: $${(kpis?.profit?.value || 0).toLocaleString()}`}
        />
        <KPICard
          title="Active Faults"
          value={kpis?.faults?.value || 0}
          icon={AlertTriangle}
          status={kpis?.faults?.status}
          description="Machines requiring attention"
        />
      </div>

      {/* Secondary KPI Cards */}
      <div className="grid gap-4 md:grid-cols-3 mb-6">
        <KPICard
          title="Availability"
          value={kpis?.availability?.value || 0}
          unit="%"
          icon={Activity}
          status={kpis?.availability?.status}
          target={kpis?.availability?.target}
        />
        <KPICard
          title="Performance"
          value={kpis?.performance?.value || 0}
          unit="%"
          icon={TrendingUp}
          status={kpis?.performance?.status}
          target={kpis?.performance?.target}
        />
        <KPICard
          title="Quality"
          value={kpis?.quality?.value || 0}
          unit="%"
          icon={CheckCircle2}
          status={kpis?.quality?.status}
          target={kpis?.quality?.target}
        />
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>OEE Breakdown</CardTitle>
            <CardDescription>Availability × Performance × Quality</CardDescription>
          </CardHeader>
          <CardContent>
            <OEEGauge 
              oee={kpis?.oee?.value || 0}
              availability={kpis?.availability?.value || 0}
              performance={kpis?.performance?.value || 0}
              quality={kpis?.quality?.value || 0}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Production Rate</CardTitle>
            <CardDescription>Parts produced per hour</CardDescription>
          </CardHeader>
          <CardContent>
            <SimpleBarChart data={productionData} label="Parts/Hour" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Machine Status</CardTitle>
            <CardDescription>Current state distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <SimpleBarChart data={machineStatusData} label="Machines" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Financial Summary</CardTitle>
            <CardDescription>Revenue vs Costs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Revenue</span>
                <span className="text-lg font-semibold text-green-500">
                  ${(kpis?.revenue?.value || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Cost</span>
                <span className="text-lg font-semibold text-red-500">
                  ${(kpis?.cost?.value || 0).toLocaleString()}
                </span>
              </div>
              <div className="border-t border-border pt-4 flex justify-between items-center">
                <span className="text-sm font-medium">Net Profit</span>
                <span className={`text-xl font-bold ${(kpis?.profit?.value || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  ${(kpis?.profit?.value || 0).toLocaleString()}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                Profit Margin: {kpis?.revenue?.value ? ((kpis?.profit?.value / kpis?.revenue?.value) * 100).toFixed(1) : 0}%
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
